import { z } from 'zod';
export const blogPostSchema = z.object({
  id: z.string(),
  slug: z.string().min(1),
  title: z.string(),
  excerpt: z.string(),
  content: z.string(),
  author: z.string().optional(),
  lang: z.string().optional(),
  publishedAt: z.string(),
  updatedAt: z.string().optional(),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  featuredImage: z.string().optional(),
  seoTitle: z.string().optional(),
  seoDescription: z.string().optional(),
  published: z.boolean(),
  status: z.enum(['published', 'scheduled', 'draft']).optional()
});
export const schemas = {
  blog: z.array(blogPostSchema).superRefine((posts, ctx) => {
    const seen = new Set<string>();
    posts.forEach((p, i) => {
      if (seen.has(p.slug)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate blog slug "${p.slug}"`,
          path: [i, 'slug']
        });
      }
      seen.add(p.slug);
    });
  }),
  home: z.object({
    "hero": z.object({
      "headline": z.string(),
      "subheadline": z.string(),
      "cta": z.string(),
      "ctaSecondary": z.string(),
      "trustItems": z.array(z.string())
    }),
    "destinations": z.object({
      "sectionLabel": z.string(),
      "heading": z.string(),
      "subheading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "name": z.string(),
        "region": z.string(),
        "teaser": z.string(),
        "size": z.string()
      }))
    }),
    "services": z.object({
      "sectionLabel": z.string(),
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "title": z.string(),
        "description": z.string(),
        "cta": z.string()
      }))
    }),
    "testimonials": z.object({
      "sectionLabel": z.string(),
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "quote": z.string(),
        "name": z.string(),
        "destination": z.string(),
        "rating": z.number()
      }))
    }),
    "cta": z.object({
      "heading": z.string(),
      "subheading": z.string(),
      "formHeading": z.string(),
      "namePlaceholder": z.string(),
      "destinationPlaceholder": z.string(),
      "emailPlaceholder": z.string(),
      "submitLabel": z.string()
    })
  }),
  destinations: z.array(z.object({
    "id": z.string(),
    "name": z.string(),
    "tagline": z.string(),
    "description": z.string(),
    "region": z.string(),
    "type": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "highlights": z.array(z.string())
  })),
  services: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "services": z.array(z.object({
      "id": z.string(),
      "image": z.string(),
      "title": z.string(),
      "tagline": z.string(),
      "description": z.string(),
      "features": z.array(z.string()),
      "idealFor": z.string(),
      "startingFrom": z.string()
    })),
    "process": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "steps": z.array(z.object({
        "number": z.string(),
        "title": z.string(),
        "description": z.string(),
        "id": z.string()
      }))
    }),
    "cta": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "primaryLabel": z.string(),
      "secondaryLabel": z.string()
    }),
    "trustBar": z.array(z.object({
      "value": z.string(),
      "label": z.string(),
      "id": z.string()
    }))
  }),
  quote: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "form": z.object({
      "heading": z.string(),
      "submitLabel": z.string(),
      "successTitle": z.string(),
      "successMessage": z.string(),
      "tripTypes": z.array(z.string()),
      "budgetRanges": z.array(z.string()),
      "travelerCounts": z.array(z.string())
    }),
    "sidebar": z.object({
      "whyUs": z.object({
        "title": z.string(),
        "points": z.array(z.string())
      }),
      "contact": z.object({
        "label": z.string(),
        "phone": z.string(),
        "email": z.string(),
        "hours": z.string()
      })
    })
  }),
  about: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "story": z.object({
      "heading": z.string(),
      "paragraphs": z.array(z.string())
    }),
    "founder": z.object({
      "eyebrow": z.string(),
      "name": z.string(),
      "role": z.string(),
      "quote": z.string(),
      "bio1": z.string(),
      "bio2": z.string(),
      "credentials": z.array(z.string())
    }),
    "values": z.object({
      "heading": z.string(),
      "items": z.array(z.object({
        "title": z.string(),
        "description": z.string(),
        "id": z.string()
      }))
    }),
    "services": z.object({
      "eyebrow": z.string(),
      "heading": z.string(),
      "intro": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "title": z.string(),
        "description": z.string()
      }))
    }).optional(),
    "testimonials": z.object({
      "eyebrow": z.string(),
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "quote": z.string(),
        "name": z.string(),
        "trip": z.string()
      }))
    }).optional(),
    "team": z.object({
      "heading": z.string(),
      "subtitle": z.string(),
      "members": z.array(z.object({
        "name": z.string(),
        "image": z.string(),
        "role": z.string(),
        "bio": z.string(),
        "specialties": z.string(),
        "id": z.string()
      }))
    }).optional(),
    "stats": z.array(z.object({
      "value": z.string(),
      "label": z.string(),
      "id": z.string()
    })),
    "cta": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "primaryLabel": z.string(),
      "secondaryLabel": z.string()
    }),
    "faq": z.object({
      "eyebrow": z.string(),
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "question": z.string(),
        "answer": z.string()
      }))
    })
  }),
  contact: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "info": z.object({
      "phone": z.string(),
      "email": z.string(),
      "hours": z.string(),
      "address": z.string()
    }),
    "form": z.object({
      "heading": z.string(),
      "submitLabel": z.string(),
      "successTitle": z.string(),
      "successMessage": z.string(),
      "subjects": z.array(z.string())
    }),
    "faq": z.object({
      "heading": z.string(),
      "items": z.array(z.object({
        "question": z.string(),
        "answer": z.string(),
        "id": z.string()
      }))
    })
  }),
  es_home: z.object({
    "hero": z.object({
      "headline": z.string(),
      "subheadline": z.string(),
      "cta": z.string(),
      "ctaSecondary": z.string(),
      "trustItems": z.array(z.string())
    }),
    "destinations": z.object({
      "sectionLabel": z.string(),
      "heading": z.string(),
      "subheading": z.string(),
      "viewAll": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "region": z.string(),
        "name": z.string(),
        "teaser": z.string()
      }))
    }),
    "services": z.object({
      "sectionLabel": z.string(),
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "title": z.string(),
        "description": z.string(),
        "cta": z.string()
      }))
    }),
    "testimonials": z.object({
      "sectionLabel": z.string(),
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "rating": z.number(),
        "quote": z.string(),
        "name": z.string(),
        "destination": z.string()
      }))
    }),
    "cta": z.object({
      "eyebrow": z.string(),
      "heading": z.string(),
      "subheading": z.string(),
      "formHeading": z.string(),
      "namePlaceholder": z.string(),
      "destinationPlaceholder": z.string(),
      "emailPlaceholder": z.string(),
      "submitLabel": z.string(),
      "disclaimer": z.string()
    })
  }),
  es_services: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "services": z.array(z.object({
      "id": z.string(),
      "image": z.string(),
      "title": z.string(),
      "tagline": z.string(),
      "description": z.string(),
      "features": z.array(z.string()),
      "idealFor": z.string()
    })),
    "process": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "steps": z.array(z.object({
        "id": z.string(),
        "number": z.string(),
        "title": z.string(),
        "description": z.string()
      }))
    }),
    "cta": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "primaryLabel": z.string(),
      "secondaryLabel": z.string()
    }),
    "trustBar": z.array(z.object({
      "value": z.string(),
      "label": z.string(),
      "id": z.string()
    }))
  }),
  es_about: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "story": z.object({
      "heading": z.string(),
      "paragraphs": z.array(z.string())
    }),
    "founder": z.object({
      "eyebrow": z.string(),
      "name": z.string(),
      "role": z.string(),
      "quote": z.string(),
      "bio1": z.string(),
      "bio2": z.string(),
      "credentials": z.array(z.string())
    }),
    "values": z.object({
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "title": z.string(),
        "description": z.string()
      }))
    }),
    "team": z.object({
      "heading": z.string(),
      "subtitle": z.string(),
      "members": z.array(z.object({
        "id": z.string(),
        "name": z.string(),
        "image": z.string(),
        "role": z.string(),
        "bio": z.string(),
        "specialties": z.string()
      }))
    }).optional(),
    "stats": z.array(z.object({
      "id": z.string(),
      "value": z.string(),
      "label": z.string()
    })),
    "cta": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "primaryLabel": z.string(),
      "secondaryLabel": z.string()
    }),
    "faq": z.object({
      "eyebrow": z.string(),
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "question": z.string(),
        "answer": z.string()
      }))
    })
  }),
  es_contact: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "info": z.object({
      "phone": z.string(),
      "email": z.string(),
      "hours": z.string(),
      "address": z.string()
    }),
    "form": z.object({
      "heading": z.string(),
      "submitLabel": z.string(),
      "successTitle": z.string(),
      "successMessage": z.string(),
      "subjects": z.array(z.string())
    }),
    "faq": z.object({
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "question": z.string(),
        "answer": z.string()
      }))
    })
  }),
  es_quote: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "form": z.object({
      "heading": z.string(),
      "submitLabel": z.string(),
      "successTitle": z.string(),
      "successMessage": z.string(),
      "tripTypes": z.array(z.string()),
      "budgetRanges": z.array(z.string()),
      "travelerCounts": z.array(z.string())
    }),
    "sidebar": z.object({
      "whyUs": z.object({
        "title": z.string(),
        "points": z.array(z.string())
      }),
      "contact": z.object({
        "label": z.string(),
        "phone": z.string(),
        "email": z.string(),
        "hours": z.string()
      })
    })
  }),
  messaging_policy: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "meta": z.object({
      "lastUpdated": z.string()
    }),
    "dataSharing": z.object({
      "heading": z.string(),
      "items": z.array(z.string())
    }),
    "terms": z.object({
      "heading": z.string(),
      "sections": z.array(z.object({
        "id": z.string(),
        "number": z.string(),
        "text": z.string()
      }))
    })
  }),
  es_messaging_policy: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "meta": z.object({
      "lastUpdated": z.string()
    }),
    "dataSharing": z.object({
      "heading": z.string(),
      "items": z.array(z.string())
    }),
    "terms": z.object({
      "heading": z.string(),
      "sections": z.array(z.object({
        "id": z.string(),
        "number": z.string(),
        "text": z.string()
      }))
    })
  }),
  destination_caribbean: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "experiences": z.array(z.object({
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "practicalTips": z.array(z.object({
      "heading": z.string(),
      "body": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    })),
    "quoteLabel": z.string()
  }),
  destination_bali: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "experiences": z.array(z.object({
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "practicalTips": z.array(z.object({
      "heading": z.string(),
      "body": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    })),
    "quoteLabel": z.string()
  }),
  destination_maldives: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "experiences": z.array(z.object({
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "practicalTips": z.array(z.object({
      "heading": z.string(),
      "body": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    })),
    "quoteLabel": z.string()
  }),
  destination_santorini: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "experiences": z.array(z.object({
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "practicalTips": z.array(z.object({
      "heading": z.string(),
      "body": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    })),
    "quoteLabel": z.string()
  }),
  destination_costa_rica: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "experiences": z.array(z.object({
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "practicalTips": z.array(z.object({
      "heading": z.string(),
      "body": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    })),
    "quoteLabel": z.string()
  }),
  destination_alaska_cruise: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "experiences": z.array(z.object({
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "practicalTips": z.array(z.object({
      "heading": z.string(),
      "body": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    })),
    "quoteLabel": z.string()
  }),
  destino_caribe: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    }))
  }),
  destino_bali: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    }))
  }),
  destino_maldivas: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    }))
  }),
  destino_santorini: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    }))
  }),
  destino_costa_rica: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    }))
  }),
  destino_crucero_alaska: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "priceFrom": z.string().optional(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    }))
  }),
  destination_amalfi_coast: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "experiences": z.array(z.object({
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "practicalTips": z.array(z.object({
      "heading": z.string(),
      "body": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    })),
    "quoteLabel": z.string()
  }),
  destination_mediterranean_cruise: z.object({
    "name": z.string(),
    "region": z.string(),
    "tagline": z.string(),
    "heroDescription": z.string(),
    "heroImageAlt": z.string(),
    "duration": z.string(),
    "bestTime": z.string(),
    "groupSize": z.string(),
    "intro": z.string(),
    "highlights": z.array(z.object({
      "icon": z.string(),
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "experiences": z.array(z.object({
      "title": z.string(),
      "description": z.string(),
      "id": z.string()
    })),
    "practicalTips": z.array(z.object({
      "heading": z.string(),
      "body": z.string(),
      "id": z.string()
    })),
    "faqs": z.array(z.object({
      "question": z.string(),
      "answer": z.string(),
      "id": z.string()
    })),
    "quoteLabel": z.string()
  }),
  referral: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string(),
      "cta": z.string()
    }),
    "stats": z.array(z.object({
      "id": z.string(),
      "value": z.string(),
      "label": z.string()
    })),
    "howItWorks": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "steps": z.array(z.object({
        "id": z.string(),
        "number": z.string(),
        "title": z.string(),
        "description": z.string()
      }))
    }),
    "rewards": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "title": z.string(),
        "description": z.string()
      }))
    }),
    "form": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "yourNameLabel": z.string(),
      "yourEmailLabel": z.string(),
      "yourPhoneLabel": z.string(),
      "friendNameLabel": z.string(),
      "friendEmailLabel": z.string(),
      "friendPhoneLabel": z.string(),
      "messageLabel": z.string(),
      "messagePlaceholder": z.string(),
      "submitLabel": z.string(),
      "successTitle": z.string(),
      "successMessage": z.string(),
      "disclaimer": z.string()
    }),
    "faq": z.object({
      "title": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "question": z.string(),
        "answer": z.string()
      }))
    }),
    "cta": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "primaryLabel": z.string(),
      "secondaryLabel": z.string(),
      "secondaryHref": z.string()
    })
  }),
  testimonials: z.object({
    "hero": z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "stats": z.array(z.object({
      "id": z.string(),
      "value": z.string(),
      "label": z.string()
    })),
    "featured": z.object({
      "sectionLabel": z.string(),
      "title": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "quote": z.string(),
        "name": z.string(),
        "destination": z.string(),
        "tripType": z.string(),
        "rating": z.number()
      }))
    }),
    "videoSection": z.object({
      "sectionLabel": z.string(),
      "title": z.string(),
      "subtitle": z.string()
    }),
    "grid": z.object({
      "sectionLabel": z.string(),
      "title": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "quote": z.string(),
        "name": z.string(),
        "destination": z.string(),
        "tripType": z.string(),
        "rating": z.number()
      }))
    }),
    "cta": z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "primaryLabel": z.string(),
      "primaryHref": z.string(),
      "secondaryLabel": z.string(),
      "secondaryHref": z.string()
    }),
    "faq": z.object({
      "eyebrow": z.string(),
      "heading": z.string(),
      "items": z.array(z.object({
        "id": z.string(),
        "question": z.string(),
        "answer": z.string()
      }))
    })
  }),
  pages: {
    home: z.object({
      "hero": z.object({
        "headline": z.string(),
        "subheadline": z.string(),
        "cta": z.string(),
        "ctaSecondary": z.string(),
        "trustItems": z.array(z.string())
      }),
      "destinations": z.object({
        "sectionLabel": z.string(),
        "heading": z.string(),
        "subheading": z.string(),
        "items": z.array(z.object({
          "id": z.string(),
          "name": z.string(),
          "region": z.string(),
          "teaser": z.string(),
          "size": z.string()
        }))
      }),
      "services": z.object({
        "sectionLabel": z.string(),
        "heading": z.string(),
        "items": z.array(z.object({
          "id": z.string(),
          "title": z.string(),
          "description": z.string(),
          "cta": z.string()
        }))
      }),
      "testimonials": z.object({
        "sectionLabel": z.string(),
        "heading": z.string(),
        "items": z.array(z.object({
          "id": z.string(),
          "quote": z.string(),
          "name": z.string(),
          "destination": z.string(),
          "rating": z.number()
        }))
      }),
      "cta": z.object({
        "heading": z.string(),
        "subheading": z.string(),
        "formHeading": z.string(),
        "nameLabel": z.string(),
        "namePlaceholder": z.string(),
        "destinationLabel": z.string(),
        "destinationPlaceholder": z.string(),
        "emailLabel": z.string(),
        "emailPlaceholder": z.string(),
        "submitLabel": z.string()
      })
    }),
    es_blog: z.object({
      "eyebrow": z.string(),
      "title": z.string(),
      "subtitle": z.string(),
      "article": z.string(),
      "articles": z.string()
    }),
    faq: z.object({
      "hero": z.object({
        "eyebrow": z.string(),
        "heading": z.string(),
        "subheading": z.string()
      }),
      "cta": z.object({
        "heading": z.string(),
        "body": z.string(),
        "buttonLabel": z.string()
      })
    }),
    lp_bilingual: z.object({
      "meta": z.object({
        "title": z.string(),
        "description": z.string(),
        "ogTitle": z.string(),
        "ogDescription": z.string()
      }),
      "urgencyBar": z.object({
        "text": z.string()
      }),
      "hero": z.object({
        "badge": z.string(),
        "heading": z.string(),
        "headingAccent": z.string(),
        "subheading": z.string(),
        "subheadingEs": z.string(),
        "ctaPrimary": z.string(),
        "ctaPhone": z.string(),
        "ctaWhatsapp": z.string(),
        "trustRating": z.string(),
        "trustDestinations": z.string()
      }),
      "trustBar": z.object({
        "items": z.array(z.object({
          "id": z.string(),
          "heading": z.string(),
          "body": z.string()
        }))
      }),
      "whyBilingual": z.object({
        "heading": z.string(),
        "subheading": z.string(),
        "reasons": z.array(z.object({
          "id": z.string(),
          "title": z.string(),
          "desc": z.string()
        }))
      }),
      "destinations": z.object({
        "heading": z.string(),
        "subheading": z.string(),
        "items": z.array(z.object({
          "id": z.string(),
          "name": z.string(),
          "emoji": z.string(),
          "desc": z.string()
        }))
      }),
      "testimonials": z.object({
        "heading": z.string(),
        "items": z.array(z.object({
          "id": z.string(),
          "name": z.string(),
          "location": z.string(),
          "text": z.string(),
          "rating": z.number()
        }))
      }),
      "quoteForm": z.object({
        "heading": z.string(),
        "subheading": z.string(),
        "subheadingEs": z.string(),
        "successHeading": z.string(),
        "successBody": z.string(),
        "successLink": z.string(),
        "labelName": z.string(),
        "labelEmail": z.string(),
        "labelPhone": z.string(),
        "labelDestination": z.string(),
        "labelMessage": z.string(),
        "placeholderMessage": z.string(),
        "consent": z.string(),
        "submitIdle": z.string(),
        "submitBusy": z.string()
      }),
      "finalCta": z.object({
        "eyebrow": z.string(),
        "phone": z.string(),
        "whatsapp": z.string(),
        "tagline": z.string()
      }),
      "ended": z.object({
        "heading": z.string(),
        "body": z.string(),
        "cta": z.string()
      }),
      "comingSoon": z.object({
        "heading": z.string(),
        "body": z.string(),
        "cta": z.string()
      })
    }),
    ad_performance: z.object({
      "title": z.string(),
      "subtitle": z.string(),
      "notConfigured": z.object({
        "heading": z.string(),
        "body": z.string(),
        "tokenLabel": z.string(),
        "tokenHint": z.string(),
        "accountLabel": z.string(),
        "accountHint": z.string()
      }),
      "dateRanges": z.object({
        "last7d": z.string(),
        "last30d": z.string(),
        "last90d": z.string()
      }),
      "summary": z.object({
        "totalSpend": z.string(),
        "impressions": z.string(),
        "clicks": z.string(),
        "avgCtr": z.string(),
        "ctrSub": z.string(),
        "avgCpc": z.string(),
        "cpcSub": z.string()
      }),
      "table": z.object({
        "heading": z.string(),
        "colCampaign": z.string(),
        "colSpend": z.string(),
        "colImpressions": z.string(),
        "colClicks": z.string(),
        "colCtr": z.string(),
        "colCpc": z.string(),
        "colStatus": z.string(),
        "colObjective": z.string(),
        "empty": z.string(),
        "footer": z.string()
      })
    })
  }
};
export type Schemas = typeof schemas;